import { rd } from '@passionware/monads';
import { createReadWriteApiStore } from 'v5/platform/react/readWriteApiStore';
import { createLocalStorageApi } from 'v5/platform/ts/localStorageApi';
import {
  ProductionSearchQuery,
  productionQueryUtils,
  productionSearchQuerySchema
} from 'v5/api/production/production.api';
import {
  ResourceSearchQuery,
  resourceQueryUtils,
  resourceSearchQuerySchema
} from 'v5/api/resource/resource.api';
import { z } from 'zod';
import {
  BookingConfirmationTemplateEditorPreference,
  EntityListLayoutPreference,
  FeatureFlagManagerPreference,
  ProductionsListViewPreference,
  ShortcutMapping,
  UserPreferencesService
} from './UserPreferencesService';

function parseCallsheetProductionQuery(value: unknown): ProductionSearchQuery {
  // The callsheet picker has its own first-open default state.
  if (value == null) {
    return productionQueryUtils.ofDefault();
  }

  return productionSearchQuerySchema.parse(value);
}

function parseResourcingAssignResourceQuery(
  value: unknown
): ResourceSearchQuery {
  if (value == null) {
    return resourceQueryUtils.ofDefault();
  }

  return resourceSearchQuerySchema.parse(value) as ResourceSearchQuery;
}

const productionsListViewPreferenceSchema = z.object({
  showResourcingBudgetColumn: z.boolean(),
  budgetChartMode: z.enum(['vsPlanned', 'vsBudget']).optional()
});

function parseProductionsListView(
  value: unknown,
  defaults: ProductionsListViewPreference
): ProductionsListViewPreference {
  if (value == null) {
    return defaults;
  }

  const parsed = productionsListViewPreferenceSchema.safeParse(value);
  if (!parsed.success) {
    return defaults;
  }

  return {
    ...defaults,
    ...parsed.data,
    budgetChartMode: parsed.data.budgetChartMode ?? defaults.budgetChartMode
  };
}

export function createUserPreferencesService(): UserPreferencesService {
  const defaultEntityListLayout: EntityListLayoutPreference = {
    viewMode: 'list',
    tileMinWidthPx: 280
  };
  const useEntityListLayout = createReadWriteApiStore(
    createLocalStorageApi(
      'entity-list-layout',
      z.record(
        z.string(),
        z.object({
          viewMode: z.enum(['grid', 'list']),
          tileMinWidthPx: z.number()
        })
      ).parse,
      {}
    )
  );
  const callsheetsProductionQuery = createReadWriteApiStore(
    createLocalStorageApi(
      'callsheets-production-query',
      parseCallsheetProductionQuery,
      productionQueryUtils.ofDefault()
    )
  );
  const resourcingAssignResourceQuery = createReadWriteApiStore(
    createLocalStorageApi(
      'resourcing-assign-resource-query',
      parseResourcingAssignResourceQuery,
      resourceQueryUtils.ofDefault()
    )
  );
  const defaultProductionsListView: ProductionsListViewPreference = {
    showResourcingBudgetColumn: false,
    budgetChartMode: 'vsPlanned'
  };
  const productionsListView = createReadWriteApiStore(
    createLocalStorageApi(
      'productions-list-view',
      value => parseProductionsListView(value, defaultProductionsListView),
      defaultProductionsListView
    )
  );
  const defaultBookingConfirmationTemplateEditor: BookingConfirmationTemplateEditorPreference =
    {
      viewMode: 'split'
    };
  const bookingConfirmationTemplateEditorSchema = z.object({
    viewMode: z.enum(['primary', 'secondary', 'split'])
  });
  const bookingConfirmationTemplateEditor = createReadWriteApiStore(
    createLocalStorageApi(
      'booking-confirmation-template-editor',
      value => {
        if (value == null) {
          return defaultBookingConfirmationTemplateEditor;
        }
        const parsed = bookingConfirmationTemplateEditorSchema.safeParse(value);
        return parsed.success
          ? parsed.data
          : defaultBookingConfirmationTemplateEditor;
      },
      defaultBookingConfirmationTemplateEditor
    )
  );
  const defaultFeatureFlagManager: FeatureFlagManagerPreference = {
    search: '',
    starredFeatureIds: [],
    browseBy: 'feature'
  };
  const featureFlagManagerPreferenceSchema = z.object({
    search: z.string(),
    starredFeatureIds: z.array(z.string()),
    browseBy: z.enum(['feature', 'workspace']).default('feature')
  });
  const featureFlagManager = createReadWriteApiStore(
    createLocalStorageApi(
      'feature-flag-manager',
      value => {
        if (value == null) {
          return defaultFeatureFlagManager;
        }
        const parsed = featureFlagManagerPreferenceSchema.safeParse(value);
        return parsed.success ? parsed.data : defaultFeatureFlagManager;
      },
      defaultFeatureFlagManager
    )
  );
  const useEntityTableColumns = createReadWriteApiStore(
    createLocalStorageApi(
      'entity-table-columns',
      z.record(z.string(), z.array(z.string())).parse,
      {}
    )
  );
  const useCollapsedFormGroups = createReadWriteApiStore(
    createLocalStorageApi(
      'collapsed-form-groups',
      z.record(z.string(), z.literal(true)).parse,
      {}
    )
  );
  return {
    useShortcuts: () => {
      // TODO: Load from localStorage or backend in the future
      const shortcuts: ShortcutMapping = {
        create: {
          production: 'mod+alt+p',
          shortlist: 'mod+alt+s',
          booking: 'mod+alt+b',
          callsheet: 'mod+alt+c',
          callout: 'mod+alt+o',
          resource: 'mod+alt+r'
        }
      };

      return rd.of(shortcuts);
    },

    forEntityListLayout: key => ({
      useValue: () =>
        rd.map(
          useEntityListLayout.useValue(),
          data => data[key ?? 'default'] ?? defaultEntityListLayout
        ),
      ensureValue: async () =>
        rd.map(
          await useEntityListLayout.ensureValue(),
          data => data[key ?? 'default'] ?? defaultEntityListLayout
        ),
      setValue: async value =>
        useEntityListLayout.setValue({
          ...rd.getOrThrow(
            await useEntityListLayout.ensureValue(),
            'Attempted to set value when preference is not loaded'
          ),
          [key ?? 'default']: value
        })
    }),

    forEntityTableColumns: scopeKey => ({
      useValue: () =>
        rd.map(useEntityTableColumns.useValue(), data => data[scopeKey]),
      ensureValue: async () =>
        rd.map(
          await useEntityTableColumns.ensureValue(),
          data => data[scopeKey]
        ),
      setValue: async value => {
        const data = rd.getOrThrow(
          await useEntityTableColumns.ensureValue(),
          'Attempted to set entity table columns when preference is not loaded'
        );
        if (value === undefined) {
          const { [scopeKey]: _removed, ...rest } = data;
          useEntityTableColumns.setValue(rest);
          return;
        }
        useEntityTableColumns.setValue({
          ...data,
          [scopeKey]: value
        });
      }
    }),
    forCollapsedFormGroup: key => ({
      useValue: () =>
        rd.map(useCollapsedFormGroups.useValue(), data => data[key] === true),
      ensureValue: async () =>
        rd.map(
          await useCollapsedFormGroups.ensureValue(),
          data => data[key] === true
        ),
      setValue: async collapsed => {
        const data = rd.getOrThrow(
          await useCollapsedFormGroups.ensureValue(),
          'Attempted to set a collapsed form group when preference is not loaded'
        );
        const { [key]: _previous, ...rest } = data;
        useCollapsedFormGroups.setValue(
          collapsed ? { ...rest, [key]: true } : rest
        );
      }
    }),
    productionsListView,
    bookingConfirmationTemplateEditor,
    callsheetsProductionQuery,
    resourcingAssignResourceQuery,
    featureFlagManager
  };
}
